import { useContext, useMemo } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import TeamContext, {
  Section,
  View,
} from '@atom/components/teamPortal/TeamContext';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  INIT_PAGINATION,
  useTeamNavigation,
} from '@atom/hooks/useTeamNavigation';
import { Icon, IconButton } from '@atom/mui';
import { PolicyAction } from '@atom/types/policy';
import { TimeEntryApproverInfo } from '@atom/types/user';
import { hasAccess } from '@atom/utilities/accessUtilities';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';
import naturalSort from '@atom/utilities/naturalSortCollator';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import UserGroupTree from './userGroupTree/UserGroupTree';

import classes from './teamSidebar.module.css';

const TeamSidebar = () => {
  const { teamNavigation } = useTeamNavigation();
  const preferences = usePreferences();
  const [{ view, teamId, timeSheetUserId }, { updateSearchParams }] =
    useNavigation({
      view: SearchParamType.STRING,
      teamId: SearchParamType.STRING,
      timeSheetUserId: SearchParamType.STRING,
    });

  const {
    timeEntryApproverInfo,
    userLoading,
    currentUserHasUserGroups,
    toggleExpanded,
    activeTimeSheetGroup,
    setActiveTimeSheetGroup,
  } = useContext(TeamContext);

  const { expanded } = teamNavigation;

  const getClassName = (checkView: View) =>
    clsx(
      classes.sidebarRow,
      checkView === view &&
        !(checkView === View.TIME_SHEET && timeSheetUserId) &&
        classes.selected,
    );

  const handleGroupClick = (userGroup: any) => {
    updateSearchParams(
      {
        teamId: userGroup?.id,
        view: View.TIME_SHEET_LIST,
        page: INIT_PAGINATION.page,
        limit: INIT_PAGINATION.limit,
        activeGroupId: null,
        activePathIds: null,
      },
      false,
    );
  };

  const handleMyTimeSheetClick = () => {
    updateSearchParams(
      {
        view: View.TIME_SHEET,
        timeSheetUserId: null,
        timeSheetUserName: null,
        teamId: null,
        activePathIds: null,
        activeGroupId: null,
      },
      false,
    );
    setActiveTimeSheetGroup(null);
  };

  const handlePeopleManagementClick = (newView: View) => {
    updateSearchParams(
      {
        view: newView,
        page: INIT_PAGINATION.page,
        limit: INIT_PAGINATION.limit,
        activePathIds: null,
        activeGroupId: null,
      },
      false,
    );
  };

  const peopleExpandedIcon = expanded.has(Section.PEOPLE)
    ? 'expand_less'
    : 'expand_more';

  const timeExpandedIcon = expanded.has(Section.TIME_SHEET)
    ? 'expand_less'
    : 'expand_more';

  const sortByPath = (
    first: TimeEntryApproverInfo,
    second: TimeEntryApproverInfo,
  ): number => {
    return naturalSort(
      first?.groupPath.join(' / '),
      second?.groupPath.join(' / '),
    );
  };

  const readableTimeEntryUserGroupsSorted = useMemo(
    () =>
      R.sort(
        sortByPath,
        timeEntryApproverInfo.filter(group =>
          hasAccess(PolicyAction.READ, group?.actions),
        ),
      ),
    [timeEntryApproverInfo],
  );

  const showPeopleManagement = expanded.has(Section.PEOPLE);
  const showTeamTimeSheet =
    !isNilOrEmpty(readableTimeEntryUserGroupsSorted) &&
    !userLoading &&
    expanded.has(Section.TIME_SHEET);
  const showMyTimeSheet =
    currentUserHasUserGroups && expanded.has(Section.TIME_SHEET);
  const showTimeSheetSection =
    preferences?.timeTracking?.timesheet &&
    ((!isNilOrEmpty(readableTimeEntryUserGroupsSorted) && !userLoading) ||
      currentUserHasUserGroups);

  return (
    <div className={classes.sidebarContainer}>
      <UserGroupTree />
      <div className={classes.sidebarSection}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionTitle}>People Management</div>
          <IconButton onClick={() => toggleExpanded(Section.PEOPLE)}>
            <Icon>{peopleExpandedIcon}</Icon>
          </IconButton>
        </div>
        {showPeopleManagement && (
          <>
            <div
              className={getClassName(View.ACTIVE)}
              onClick={() => handlePeopleManagementClick(View.ACTIVE)}
            >
              <Icon>contacts</Icon>
              <div className={classes.rowTitle} data-cy="activeUsers">
                Active Users
              </div>
            </div>
            <div
              className={getClassName(View.PENDING)}
              onClick={() => handlePeopleManagementClick(View.PENDING)}
            >
              <Icon>person_add</Icon>
              <div className={classes.rowTitle} data-cy="pendingRequests">
                Pending Requests
              </div>
            </div>
            <div
              className={getClassName(View.DEACTIVATED)}
              onClick={() => handlePeopleManagementClick(View.DEACTIVATED)}
            >
              <Icon>not_interested</Icon>
              <div className={classes.rowTitle} data-cy="deactivatedUsers">
                Deactivated Users
              </div>
            </div>
            <div
              className={getClassName(View.ROLES)}
              onClick={() => handlePeopleManagementClick(View.ROLES)}
            >
              <Icon>account_circle</Icon>
              <div className={classes.rowTitle} data-cy="roles">
                Roles
              </div>
            </div>
          </>
        )}
      </div>
      {showTimeSheetSection && (
        <div className={classes.sidebarSection}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitle}>Time Management</div>
            <IconButton
              onClick={() => toggleExpanded(Section.TIME_SHEET)}
              data-cy="timeManagementSectionExpandCollapseButton"
            >
              <Icon>{timeExpandedIcon}</Icon>
            </IconButton>
          </div>
          {showMyTimeSheet && (
            <div
              className={getClassName(View.TIME_SHEET)}
              onClick={handleMyTimeSheetClick}
            >
              <Icon>schedule</Icon>
              <div className={classes.rowTitle} data-cy="myTimesheet">
                My Timesheet
              </div>
            </div>
          )}
          {showTeamTimeSheet && (
            <>
              <div className={classes.sidebarRow}>
                <Icon>group</Icon>
                <div className={classes.rowTitle} data-cy="teamTimesheet">
                  Team Timesheet
                </div>
              </div>
              {readableTimeEntryUserGroupsSorted.map(userGroup => {
                const color = getColorFromColorId(userGroup.colorId);
                const isActiveGroup =
                  (view === View.TIME_SHEET_LIST || view === View.TIME_SHEET) &&
                  teamId &&
                  activeTimeSheetGroup?.id === userGroup.id;

                const rowClassName = clsx(
                  classes.sidebarRowTimeGroup,
                  classes.sub,
                  isActiveGroup && classes.selected,
                );

                return (
                  <div
                    key={userGroup.id}
                    className={rowClassName}
                    onClick={() => handleGroupClick(userGroup)}
                    data-cy={userGroup.id}
                  >
                    <Icon color={color}>lens</Icon>
                    <div className={classes.rowTitle}>
                      {userGroup.groupPath.join(' / ')}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSidebar;
